import type { UiMessageModule } from "./merge";

export const publicPageMessages = {
	en: {
		public: {
			common: {
				addedToGateway: "Added to doteb",
				allUseCases: "All use cases",
				backToAllModels: "Back to all models",
				backToMigrationGuides: "Back to migration guides",
				bookDemo: "Book a Demo",
				bookWalkthrough: "Book a walkthrough",
				browseModels: "Browse Models",
				capabilities: "Capabilities",
				clone: "Clone",
				comingSoon: "Coming Soon",
				context: "context",
				contextSize: "Context Size",
				copied: "Copied!",
				copyModelId: "Copy model ID",
				copyUrl: "Copy URL",
				createFreeAccount: "Create Free Account",
				deactivated: "DEACTIVATED",
				deprecated: "DEPRECATED",
				enterpriseCapability: "Enterprise capability",
				exploreAllModels: "Explore All Models",
				exploreUseCases: "Explore use cases",
				featured: "Featured",
				free: "Free",
				getStarted: "Get started",
				getStartedFree: "Get Started Free",
				github: "GitHub",
				inputTokens: "input tokens",
				lastFourHours: "Last 4 hours",
				live: "Live",
				liveDemo: "Live Demo",
				model: "model",
				modelId: "Model ID",
				models: "models",
				month: "/mo",
				new: "New",
				no: "No",
				outputTokens: "output tokens",
				pricing: "Pricing",
				provider: "Provider",
				providerRelease: "Provider release",
				providers: "Providers",
				readGuide: "Read guide",
				readMore: "Read more",
				requests: "requests",
				share: "Share",
				stability: "Stability",
				stable: "STABLE",
				startBuilding: "Start Building",
				startFree: "Start free",
				startForFree: "Start for free",
				talkToSales: "Talk to Sales",
				tryInPlayground: "Try in Playground",
				unknown: "Unknown",
				viewDocs: "View Documentation",
				viewModelDetails: "View model details",
				viewModels: "View models",
				viewQuickStartGuide: "View Quick Start Guide",
				website: "Website",
				yes: "Yes",
			},
			capabilities: {
				embedding: "Embeddings",
				imageGeneration: "Image Generation",
				jsonOutput: "JSON Output",
				jsonSchema: "JSON Schema",
				reasoning: "Reasoning",
				streaming: "Streaming",
				tools: "Tools",
				videoGeneration: "Video Generation",
				vision: "Vision",
				webSearch: "Web Search",
			},
			pricing: {
				hero: {
					badge: "Pricing",
					title: "Simple, Transparent Pricing",
					descriptionPrefix:
						"Start free with no credit card. Pay only for what you use with transparent pricing.",
					playgroundLink: "Try models in our Playground",
					descriptionSuffix: "before committing.",
				},
				table: {
					plans: {
						free: "Free",
						forever: "forever",
						enterprise: "Enterprise",
						custom: "Custom",
						contactUs: "Contact us",
					},
					cta: {
						getStarted: "Get Started",
						contactSales: "Contact Sales",
					},
					additional: {
						line1:
							"All plans include access to our API, documentation, and community support.",
						prefix: "Need a custom solution?",
					},
					features: {
						platformFees: {
							name: "Platform Fees",
							free: "5% on credit usage",
							enterprise: "Volume discounts",
						},
						models: {
							name: "Models",
							description: "210+ unique models across 25+ providers",
							learnMore: "Browse all models ->",
							free: "All 210+ models",
							enterprise: "All 210+ models",
						},
						providerChoice: {
							name: "Provider Choice",
							description: "Same model, multiple provider options",
							learnMore: "View all providers ->",
							free: "Full control + BYOK",
							enterprise: "Custom routing rules",
						},
						freeModels: {
							name: "Free Models",
							description: "Zero-cost models with rate limits",
							free: "3 (rate limited)",
							enterprise: "3 (custom limits)",
						},
						chatAndApi: {
							name: "Chat and API Access",
							description: "Access via API and Playground",
							learnMore: "View integration guides ->",
						},
						activityLogs: {
							name: "Activity Logs & Export",
						},
						dataRetention: {
							name: "Data Retention",
							free: "30 days",
							enterprise: "Unlimited",
						},
						autoRouting: {
							name: "Auto-routing & Vendor Selection",
							description: "Automatic provider routing",
						},
						budgets: {
							name: "Budgets & Spend Controls",
						},
						promptCaching: {
							name: "Prompt Caching",
							description: "Cache prompts for faster responses",
						},
						byok: {
							name: "Bring Your Own Keys (BYOK)",
							description: "Use your own provider API keys",
							free: "Included",
							enterprise: "Custom limits",
						},
						teamManagement: {
							name: "Team Management",
						},
						advancedAnalytics: {
							name: "Advanced Analytics",
						},
						adminControls: {
							name: "Admin Controls",
							description: "Enterprise-level admin features",
						},
						dataPolicyRouting: {
							name: "Data Policy-Based Routing",
							description: "Route based on data policies",
						},
						managedPolicy: {
							name: "Managed Policy Enforcement",
						},
						sso: {
							name: "SSO/SAML",
						},
						slas: {
							name: "Contractual SLAs",
						},
						whitelabel: {
							name: "Chat App (Whitelabel)",
						},
						paymentOptions: {
							name: "Payment Options",
							free: "Credit card",
							enterprise: "Invoicing options",
						},
						rateLimits: {
							name: "Rate Limits",
							description: "Paid models are not rate limited",
							free: "20 reqs/min on free models",
							enterprise: "Custom limits",
						},
						tokenPricing: {
							name: "Token Pricing",
							description:
								"Model pricing details. Non-US cards: +1.5% intl fee",
							learnMore: "See model prices ->",
							free: "Pay per token + 5% fee",
							enterprise: "Volume discounts",
						},
						support: {
							name: "Support",
							free: "Email support",
							enterprise: "24/7 SLA + dedicated support channel",
						},
					},
				},
			},
			listPages: {
				agents: {
					title: "Agents",
					description:
						"Pre-built AI agents with tool calling capabilities. Ready to integrate and extend for your specific needs.",
				},
				integrations: {
					title: "Integrations",
					description:
						"Connect doteb with your favorite tools and workflows. Access powerful AI capabilities wherever you work.",
				},
				mcp: {
					title: "MCP Server",
					description:
						"Connect your AI assistant to 200+ LLM models through the Model Context Protocol. Works with Claude Code, Cursor, and any MCP-compatible client.",
				},
				templates: {
					title: "Templates",
					description:
						"Production-ready templates to help you build AI-powered applications faster. Clone, customize, and deploy.",
				},
			},
			agents: {
				empty: "Agents coming soon. Check back later!",
				capabilitiesLabel: "Capabilities",
				moreTitle: "More Agents Coming Soon",
				moreDescription:
					"We're building more agents with different capabilities. Have an idea?",
				requestAgent: "Request an Agent",
				items: {
					weather: {
						name: "Weather Agent",
						description:
							"An intelligent AI agent that provides real-time weather information using tool calling. Demonstrates function calling patterns with doteb.",
						capabilityToolCalling: "Tool Calling",
						capabilityRealTimeData: "Real-time Data",
						capabilityNaturalLanguage: "Natural Language",
					},
					lead: {
						name: "Lead Agent",
						description:
							"A CLI AI agent that researches a person by name or email and produces a structured profile summary including bio, role, background, and verified public references. Optionally posts results through a configurable webhook.",
						capabilityWebSearch: "Web Search",
						capabilityProfileResearch: "Profile Research",
						capabilityWebhookDelivery: "Webhook Delivery",
					},
					changelog: {
						name: "Changelog Generator",
						description:
							"Generates structured changelogs from git history using the Keep a Changelog format. Analyzes git log and diff with tools to produce categorized output (Added, Changed, Fixed).",
						capabilityGitAnalysis: "Git Analysis",
						capabilityStructuredOutput: "Structured Output",
					},
					email: {
						name: "Email Drafter",
						description:
							"Drafts polished emails from rough notes or bullet points with configurable tone. Returns structured output with subject, body, and sign-off.",
						capabilityToneControl: "Tone Control",
						capabilityTextGeneration: "Text Generation",
					},
					sentiment: {
						name: "Sentiment Analyzer",
						description:
							"Analyzes text sentiment with confidence scores and key phrase extraction. Supports direct text input or file paths and classifies as positive, negative, neutral, or mixed.",
						capabilitySentimentAnalysis: "Sentiment Analysis",
						capabilityKeyPhrases: "Key Phrases",
						capabilityFileInput: "File Input",
					},
					extractor: {
						name: "Data Extractor",
						description:
							"Extracts structured entities from unstructured text including people, organizations, dates, monetary amounts, locations, emails, and phone numbers.",
						capabilityEntityExtraction: "Entity Extraction",
						capabilityNlp: "NLP",
					},
				},
			},
			templates: {
				empty: "Templates coming soon. Check back later!",
				moreTitle: "More Coming Soon",
				moreDescription: "We're working on more templates. Have a suggestion?",
				requestTemplate: "Request a Template",
				watchDemo: "Watch Demo",
				previewAlt: "{name} preview",
				items: {
					imageGeneration: {
						name: "Image Generation",
						description:
							"Generate stunning images with AI using multiple providers. Supports DALL-E, Stable Diffusion, and more through a unified API.",
					},
					chatbot: {
						name: "AI Chatbot",
						description:
							"Streaming chat interface with conversation history and model selector. Switch between LLM providers on the fly with real-time token delivery.",
					},
					ogImage: {
						name: "OG Image Generator",
						description:
							"AI-powered Open Graph image generator with live preview, multiple themes, and one-click download. Uses structured output to generate title, subtitle, and call-to-action copy.",
					},
					feedback: {
						name: "Feedback Dashboard",
						description:
							"Customer feedback sentiment analysis dashboard. Paste reviews for batch AI analysis with sentiment scores, key themes extraction, and individual review breakdowns.",
					},
					writing: {
						name: "Writing Assistant",
						description:
							"AI writing assistant with text actions including rewrite, summarize, expand, fix grammar, and change tone. Supports multiple tone presets from professional to academic.",
					},
					qa: {
						name: "QA Agent",
						description:
							"AI-powered QA testing agent that uses browser automation to interact with your running web app. Describe tests in plain English and watch it execute step-by-step with a real-time action timeline.",
					},
				},
			},
			integrations: {
				devPass: {
					description:
						"Fixed-price monthly plans for Claude Code, Cursor, Cline, and every coding tool. One API key, 200+ models, predictable billing.",
					fromPrice: "From $29/mo",
					everyModel: "Every model included",
				},
				badges: {
					planModeOnly: "Plan mode only",
				},
				items: {
					autohand:
						"Use doteb with Autohand Code for autonomous AI-powered coding in your terminal, IDE, and Slack.",
					claudeCode:
						"Use doteb with Claude Code for AI-powered terminal assistance and coding.",
					cursor:
						"Use doteb with Cursor IDE in plan mode only. Cursor's coding agent does not work with external API endpoints.",
					codex:
						"Use doteb with OpenAI's Codex CLI for AI-powered terminal coding.",
					cline:
						"Use doteb with Cline for AI-powered coding assistance in VS Code.",
					continue:
						"Use doteb with Continue's open-source AI code assistant CLI.",
					hermes:
						"Use doteb with Nous Research's Hermes Agent for terminal-based AI coding.",
					kilo: "Use doteb with Kilo Code in VS Code for autonomous AI coding with built-in provider support.",
					kimi: "Use doteb with Kimi Code CLI for autonomous terminal-based AI coding.",
					mimo: "Use doteb with MiMo Code CLI for autonomous terminal-based AI coding.",
					n8n: "Connect n8n workflow automation to doteb for AI-powered workflows.",
					opencode:
						"Use doteb with OpenCode CLI for AI-powered development workflows.",
					opencodeDesktop:
						"Use doteb with OpenCode Desktop app - connect via GUI, no config files needed.",
					openclaw:
						"Use doteb with OpenClaw for AI-powered chat across supported chat apps.",
					pi: "Use doteb with Pi coding agent for AI-powered terminal coding with any model.",
					vscode:
						"Native VS Code integration for AI-powered code completion and chat.",
				},
			},
			apps: {
				page: {
					live: "Live - refreshed every 5 minutes",
					titleLine1: "Apps shipping with",
					titleLine2: "doteb",
					description:
						"Real coding agents, real traffic. Every tool below routes through one API, ranked by tokens processed.",
					appsTracked: "apps tracked",
					tokensProcessed: "tokens processed",
					requestsRouted: "requests routed",
					listPromptPrefix: "Want your app on this list? Set",
					listPromptSuffix: "on requests to doteb.",
					readDocs: "Read the docs",
					emptyTitle: "No app traffic recorded yet",
					emptyDescription:
						"Once requests start flowing, apps will show up here ranked by tokens processed.",
				},
				grid: {
					categories: {
						all: "All",
						coding: "Coding agents",
						automation: "Automation",
						other: "Other",
					},
					searchPlaceholder: "Search apps...",
					noResultsTitle: "No apps found",
					noResultsDescription: "Try a different search or filter.",
					leaderboard: "The leaderboard",
					topThree: "Top 3 by tokens processed",
					results: "Results",
					moreApps: "More apps",
					longTail: "The long tail",
					app: "App",
					tokens: "Tokens",
					requests: "Requests",
					lastSeen: "Last seen",
					tokensShort: "tokens",
					tokensProcessed: "tokens processed",
					lastSeenPrefix: "last seen {time}",
					justNow: "just now",
					minutesAgo: "{value}m ago",
					hoursAgo: "{value}h ago",
					daysAgo: "{value}d ago",
					monthsAgo: "{value}mo ago",
				},
				devPass: {
					badge: "Trusted by every app on this leaderboard",
					titleLine1: "One key.",
					titleLine2: "Every coding agent.",
					descriptionBefore:
						"Stop juggling nine separate subscriptions. DevPass is",
					descriptionStrong: "one flat price",
					descriptionAfter:
						"for Claude Code, Cursor, Cline, OpenCode, Aider, Continue, Zed - every tool above. Same key, every agent, anywhere you code.",
					moreAgents: "and 14+ more agents working today",
					getDevPass: "Get DevPass",
					seePlans: "See plans ->",
					dropInSetup: "drop-in setup",
				},
				metadata: {
					custom: "Custom integration sending traffic through doteb.",
				},
			},
			timeline: {
				badge: "Model timeline",
				title: "doteb model support timeline",
				description:
					"See when each model was released by its provider and when it was added to doteb.",
				searchPlaceholder: "Search models, providers, IDs...",
				significant: "Significant",
				newest: "Newest",
				oldest: "Oldest",
				providerRelease: "Provider release",
				addedToGateway: "Added to doteb",
			},
			reliability: {
				hero: {
					badge: "RELIABILITY",
					uptime: "99.9999% effective uptime",
					title: "Your AI app can't afford to go down.",
					description:
						"doteb automatically routes requests to healthy providers in real time. When one goes down, your traffic seamlessly fails over - your users never notice.",
					primaryCta: "Start Routing in Minutes",
					effectiveUptime: "Effective uptime",
					downtime: "Downtime per year",
					providers: "Providers",
					failoverOverhead: "Failover overhead",
				},
				failover: {
					badge: "HOW IT WORKS",
					title: "Automatic failover in milliseconds.",
					description:
						"Every request is health-checked in real time. The moment a provider starts failing, returning 5xx, or timing out, traffic is diverted to the next healthy one - on the same request.",
					step1Title: "Provider fails",
					step1Description:
						"An upstream provider returns a 5xx, times out, or rate limits your request. We detect it within the same request cycle.",
					step2Title: "Instant re-route",
					step2Description:
						"The Gateway automatically retries the same prompt against the next healthy provider for that model, so your app does not experience additional latency.",
					step3Title: "Response delivered",
					step3Description:
						"Your user gets their answer. Our status dashboard records the incident for you - your service stays up even when providers don't.",
					worksWithEveryRequest: "Works with every request",
					noChanges: "No SDK changes. No config.",
				},
				features: {
					badge: "WHAT'S INCLUDED",
					title: "Built for production traffic.",
					description:
						"Reliability is the default - not an add-on. Every account gets the full routing engine.",
					healthTitle: "Real-time health checks",
					healthDescription:
						"Every provider is continuously probed. Unhealthy endpoints are taken out of rotation within seconds.",
					routingTitle: "Smart routing by latency",
					routingDescription:
						"Requests go to the fastest responsive provider for your region. TTFT is tracked per provider, per model.",
					regionsTitle: "Multi-region redundancy",
					regionsDescription:
						"Route across providers spread across US, EU, and APAC so a regional outage never takes you down.",
					rateLimitTitle: "Rate-limit aware",
					rateLimitDescription:
						"When a provider throttles you, traffic shifts automatically - you keep serving requests without manual intervention.",
					observableTitle: "Observable by default",
					observableDescription:
						"Uptime, error rates, and latency tracked per provider in your dashboard. Use it in audits or share with stakeholders.",
					slaTitle: "SLA reporting",
					slaDescription:
						"Export uptime and performance reports for compliance. Enterprise plans include 99.9% SLAs with credits.",
				},
				cta: {
					title: "Stop babysitting provider dashboards.",
					description:
						"Switch your base URL to doteb and get automatic failover, real-time health monitoring, and uptime reporting across 25+ providers - in one line of code.",
				},
			},
			useCases: {
				index: {
					eyebrow: "Use cases",
					title: "What you can build with doteb",
					description:
						"One OpenAI-compatible API for 200+ models - with automatic fallback, prompt caching, and per-request cost analytics. Here's what teams build on it.",
				},
				detail: {
					eyebrow: "Use case",
					ctaTitle: "One API for every model",
					ctaDescription:
						"Route across 200+ models with fallback, caching and per-request cost analytics. Start free in minutes.",
				},
				faqTitle: "Frequently asked questions",
			},
			migration: {
				index: {
					title: "Migration Guides",
					description:
						"Switch to doteb from other LLM providers with minimal code changes. Our OpenAI-compatible API makes migration straightforward.",
					providerMissingTitle: "Don't see your provider?",
					providerMissingDescription:
						"doteb's OpenAI-compatible API works with any client that supports OpenAI. Just change the base URL and API key.",
				},
				detail: {
					fromProvider: "From {provider}",
					published: "Published",
					contentLabel: "Migration guide content",
				},
			},
			features: {
				feature: "Feature",
				getStarted: "Get Started",
				keyBenefits: "Key Benefits",
				useCases: "Use Cases",
				liveDemo: "Live Demo",
				readyTitle: "Ready to get started?",
				readyDescription:
					"Join thousands of developers using doteb to power their AI applications.",
			},
			enterprise: {
				nav: "Enterprise",
				talkToSales: "Talk to sales",
				tryFree: "Try doteb free",
				whyTeams: "Why teams turn it on",
				howItWorks: "How it works",
				howTitle: "From decision to deployed in three short steps",
				realUseCases: "Real-world use cases",
				realUseCasesTitle: "Why customers actually adopt this",
				faqTitle: "Frequently asked",
				moreCapabilities: "More enterprise capabilities",
				moreCapabilitiesTitle: "The rest of the enterprise stack",
				seeOnWorkloads: "See {feature} on your real workloads",
				workloadDescription:
					"Bring a sample workload to a 30-minute call. We'll wire it up live and show you the actual experience your team will get.",
			},
			tokenCalculator: {
				hero: {
					badge: "Free LLM Token Cost Calculator",
					title: "Calculate your true LLM token costs",
					description:
						"See exactly what GPT-4o, Claude, Gemini, and 200+ models cost at official rates, then how much less you'd pay routing each request through doteb's cheapest provider.",
					models: "200+ models",
					noMarkup: "Zero platform markup",
					freeNoSignup: "Free, no signup",
				},
				form: {
					addModel: "Add another model",
					selectModel: "Select a model",
					model: "Model",
					modelPlaceholder: "Select model...",
					inputTokens: "Input tokens",
					outputTokens: "Output tokens",
					removeModel: "Remove model",
					presets: {
						light: "Light (10K/1K)",
						medium: "Medium (100K/10K)",
						heavy: "Heavy (1M/100K)",
						intensive: "Intensive (10M/1M)",
					},
				},
				results: {
					officialPrice: "Official price",
					llmGateway: "doteb",
					cheapestProvider: "Cheapest provider",
					totalOfficial: "Total official cost",
					totalGateway: "Total with doteb",
					youSave: "You save",
					noSavings: "No savings found for this mix yet",
					share: "Share",
				},
				cta: {
					highVolumeTitle: "Processing high volume?",
					highVolumeDescription:
						"Enterprise plans include volume discounts, dedicated support, custom SLAs, and extended data retention.",
					cutCostsTitle: "Ready to cut your LLM costs?",
					cutCostsDescription:
						"Start for free with no platform fees. No credit card required.",
				},
				content: {
					howTitle: "How the LLM cost calculator works",
					howDescription:
						"Estimate your monthly spend across any model in three steps, then see how much routing through doteb saves you.",
					step: "Step {number}",
					step1Title: "Add the models you use",
					step1Description:
						"Pick from 200+ LLMs across OpenAI, Anthropic, Google, Mistral, Meta, and more. Stack as many models as you want to compare side by side.",
					step2Title: "Enter your token volumes",
					step2Description:
						"Set the input and output tokens you expect to process, or start from a Light, Medium, Heavy, or Intensive preset and adjust from there.",
					step3Title: "Compare and start saving",
					step3Description:
						"Instantly see official provider pricing next to doteb's cheapest routed provider, plus the exact amount you would save each cycle.",
					understandingTitle: "Understanding LLM token costs",
					faqTitle: "Frequently asked questions",
					faqDescription:
						"Everything you need to know about estimating and lowering your LLM token costs.",
				},
			},
			nanoBanana: {
				hero: {
					badge: "Cost Simulator",
					title: "Nano Banana Pro Cost Simulator",
					description:
						"Calculate your savings on Gemini 3 Pro image generation. doteb offers {discount}% off Google AI Studio direct pricing.",
				},
				calculator: {
					label: "Estimated monthly spend on Google AI Studio",
					googleDirect: "Google AI Studio Direct",
					fullPrice: "Full price, no discount",
					discountApplied: "{discount}% discount applied",
					savingsLabel: "Your estimated monthly savings",
					savingsBadge: "{discount}% savings",
				},
				pricing: {
					badge: "Pricing Breakdown",
					title: "Detailed Price Comparison",
					metric: "Metric",
					textInput: "Text Input",
					textOutput: "Text Output",
					cachedInput: "Cached Input",
					imageOutput: "Image Output",
					perImage: "Per Image (~1K)",
					note: "doteb prices reflect a {discount}% discount off Google AI Studio direct pricing.",
				},
				cta: {
					title: "Start Saving on Image Generation Today",
					description:
						"Get {discount}% off Gemini 3 Pro image generation with doteb. No credit card required to start.",
				},
			},
		},
	},
	"zh-CN": {
		public: {
			common: {
				addedToGateway: "加入 doteb",
				allUseCases: "所有用例",
				backToAllModels: "返回全部模型",
				backToMigrationGuides: "返回迁移指南",
				bookDemo: "预约演示",
				bookWalkthrough: "预约讲解",
				browseModels: "浏览模型",
				capabilities: "能力",
				clone: "克隆",
				comingSoon: "即将推出",
				context: "上下文",
				contextSize: "上下文长度",
				copied: "已复制",
				copyModelId: "复制模型 ID",
				copyUrl: "复制 URL",
				createFreeAccount: "创建免费账号",
				deactivated: "已停用",
				deprecated: "已弃用",
				enterpriseCapability: "企业能力",
				exploreAllModels: "探索全部模型",
				exploreUseCases: "探索用例",
				featured: "精选",
				free: "免费",
				getStarted: "开始使用",
				getStartedFree: "免费开始",
				github: "GitHub",
				inputTokens: "输入 token",
				lastFourHours: "最近 4 小时",
				live: "实时",
				liveDemo: "在线演示",
				model: "个模型",
				modelId: "模型 ID",
				models: "个模型",
				month: "/月",
				new: "新",
				no: "否",
				outputTokens: "输出 token",
				pricing: "价格",
				provider: "供应商",
				providerRelease: "供应商发布时间",
				providers: "供应商",
				readGuide: "阅读指南",
				readMore: "继续阅读",
				requests: "次请求",
				share: "分享",
				stability: "稳定性",
				stable: "稳定",
				startBuilding: "开始构建",
				startFree: "免费开始",
				startForFree: "免费开始",
				talkToSales: "联系销售",
				tryInPlayground: "在 Playground 试用",
				unknown: "未知",
				viewDocs: "查看文档",
				viewModelDetails: "查看模型详情",
				viewModels: "查看模型",
				viewQuickStartGuide: "查看快速开始指南",
				website: "官网",
				yes: "是",
			},
			capabilities: {
				embedding: "向量嵌入",
				imageGeneration: "图像生成",
				jsonOutput: "JSON 输出",
				jsonSchema: "JSON Schema",
				reasoning: "推理",
				streaming: "流式输出",
				tools: "工具调用",
				videoGeneration: "视频生成",
				vision: "视觉理解",
				webSearch: "网络搜索",
			},
			pricing: {
				hero: {
					badge: "价格",
					title: "简单透明的计费",
					descriptionPrefix:
						"无需信用卡即可免费开始。按实际用量付费，价格清晰透明。",
					playgroundLink: "先在 Playground 试用模型",
					descriptionSuffix: "再决定是否接入。",
				},
				table: {
					plans: {
						free: "免费版",
						forever: "永久",
						enterprise: "企业版",
						custom: "定制",
						contactUs: "联系我们",
					},
					cta: {
						getStarted: "开始使用",
						contactSales: "联系销售",
					},
					additional: {
						line1: "所有方案都包含 API、文档和社区支持。",
						prefix: "需要定制方案？",
					},
					features: {
						platformFees: {
							name: "平台费用",
							free: "额度用量收取 5%",
							enterprise: "批量折扣",
						},
						models: {
							name: "模型",
							description: "覆盖 25+ 供应商的 210+ 唯一模型",
							learnMore: "浏览全部模型 ->",
							free: "全部 210+ 模型",
							enterprise: "全部 210+ 模型",
						},
						providerChoice: {
							name: "供应商选择",
							description: "同一模型可选择多个供应商",
							learnMore: "查看全部供应商 ->",
							free: "完全控制 + BYOK",
							enterprise: "自定义路由规则",
						},
						freeModels: {
							name: "免费模型",
							description: "有速率限制的零成本模型",
							free: "3 个（限速）",
							enterprise: "3 个（自定义限制）",
						},
						chatAndApi: {
							name: "聊天与 API 访问",
							description: "通过 API 和 Playground 使用",
							learnMore: "查看集成指南 ->",
						},
						activityLogs: {
							name: "活动日志与导出",
						},
						dataRetention: {
							name: "数据保留",
							free: "30 天",
							enterprise: "不限时长",
						},
						autoRouting: {
							name: "自动路由与供应商选择",
							description: "自动选择供应商路由",
						},
						budgets: {
							name: "预算与花费控制",
						},
						promptCaching: {
							name: "Prompt 缓存",
							description: "缓存 prompt 以获得更快响应",
						},
						byok: {
							name: "自带密钥（BYOK）",
							description: "使用你自己的供应商 API 密钥",
							free: "已包含",
							enterprise: "自定义限制",
						},
						teamManagement: {
							name: "团队管理",
						},
						advancedAnalytics: {
							name: "高级分析",
						},
						adminControls: {
							name: "管理员控制",
							description: "企业级管理功能",
						},
						dataPolicyRouting: {
							name: "基于数据策略的路由",
							description: "按数据策略决定路由",
						},
						managedPolicy: {
							name: "托管策略执行",
						},
						sso: {
							name: "SSO/SAML",
						},
						slas: {
							name: "合同 SLA",
						},
						whitelabel: {
							name: "白标聊天应用",
						},
						paymentOptions: {
							name: "支付方式",
							free: "信用卡",
							enterprise: "发票选项",
						},
						rateLimits: {
							name: "速率限制",
							description: "付费模型不限速",
							free: "免费模型 20 次请求/分钟",
							enterprise: "自定义限制",
						},
						tokenPricing: {
							name: "Token 价格",
							description: "模型价格详情。非美国银行卡：+1.5% 国际手续费",
							learnMore: "查看模型价格 ->",
							free: "按 token 付费 + 5% 费用",
							enterprise: "批量折扣",
						},
						support: {
							name: "支持",
							free: "邮件支持",
							enterprise: "24/7 SLA + 专属支持通道",
						},
					},
				},
			},
			listPages: {
				agents: {
					title: "Agents",
					description:
						"预构建的 AI agent，内置工具调用能力，可直接集成并按你的需求扩展。",
				},
				integrations: {
					title: "集成",
					description:
						"把 doteb 接入你常用的工具和工作流，在任何工作场景中使用强大的 AI 能力。",
				},
				mcp: {
					title: "MCP Server",
					description:
						"通过 Model Context Protocol 将 AI 助手连接到 200+ LLM 模型。支持 Claude Code、Cursor 和任意兼容 MCP 的客户端。",
				},
				templates: {
					title: "模板",
					description:
						"生产可用的模板，帮助你更快构建 AI 应用。克隆、定制，然后部署。",
				},
			},
			agents: {
				empty: "Agent 即将推出，请稍后再来查看。",
				capabilitiesLabel: "能力",
				moreTitle: "更多 Agent 即将推出",
				moreDescription: "我们正在构建更多不同能力的 agent。你有想法吗？",
				requestAgent: "提交 Agent 需求",
				items: {
					weather: {
						name: "Weather Agent",
						description:
							"一个智能 AI agent，通过工具调用提供实时天气信息，展示 doteb 的函数调用模式。",
						capabilityToolCalling: "工具调用",
						capabilityRealTimeData: "实时数据",
						capabilityNaturalLanguage: "自然语言",
					},
					lead: {
						name: "Lead Agent",
						description:
							"一个 CLI AI agent，可按姓名或邮箱调研人物，并生成包含简介、职位、背景和已验证公开资料的结构化画像；也可通过可配置 webhook 投递结果。",
						capabilityWebSearch: "网络搜索",
						capabilityProfileResearch: "人物调研",
						capabilityWebhookDelivery: "Webhook 投递",
					},
					changelog: {
						name: "Changelog Generator",
						description:
							"基于 git 历史生成结构化 changelog，遵循 Keep a Changelog 格式，并通过工具分析 git log 和 diff，输出 Added、Changed、Fixed 等分类。",
						capabilityGitAnalysis: "Git 分析",
						capabilityStructuredOutput: "结构化输出",
					},
					email: {
						name: "Email Drafter",
						description:
							"根据粗略笔记或要点草拟专业邮件，可配置语气，并返回主题、正文和落款等结构化结果。",
						capabilityToneControl: "语气控制",
						capabilityTextGeneration: "文本生成",
					},
					sentiment: {
						name: "Sentiment Analyzer",
						description:
							"分析文本情绪，输出置信度和关键短语；支持直接文本或文件路径输入，并分类为正面、负面、中性或混合。",
						capabilitySentimentAnalysis: "情绪分析",
						capabilityKeyPhrases: "关键短语",
						capabilityFileInput: "文件输入",
					},
					extractor: {
						name: "Data Extractor",
						description:
							"从非结构化文本中抽取结构化实体，包括人物、组织、日期、金额、地点、邮箱和电话号码。",
						capabilityEntityExtraction: "实体抽取",
						capabilityNlp: "NLP",
					},
				},
			},
			templates: {
				empty: "模板即将推出，请稍后再来查看。",
				moreTitle: "更多模板即将推出",
				moreDescription: "我们正在开发更多模板。你有建议吗？",
				requestTemplate: "提交模板需求",
				watchDemo: "观看演示",
				previewAlt: "{name} 预览",
				items: {
					imageGeneration: {
						name: "图像生成",
						description:
							"使用多个供应商生成高质量 AI 图像。通过统一 API 支持 DALL-E、Stable Diffusion 等模型。",
					},
					chatbot: {
						name: "AI Chatbot",
						description:
							"带会话历史和模型选择器的流式聊天界面，可实时切换 LLM 供应商并接收 token 流。",
					},
					ogImage: {
						name: "OG 图像生成器",
						description:
							"AI 驱动的 Open Graph 图像生成器，支持实时预览、多主题和一键下载，并用结构化输出生成标题、副标题和 CTA 文案。",
					},
					feedback: {
						name: "反馈看板",
						description:
							"客户反馈情绪分析看板。粘贴评论即可批量 AI 分析，输出情绪分数、关键主题和单条评论拆解。",
					},
					writing: {
						name: "写作助手",
						description:
							"AI 写作助手，支持改写、总结、扩写、语法修正和调整语气，并提供从专业到学术的多个语气预设。",
					},
					qa: {
						name: "QA Agent",
						description:
							"AI 驱动的 QA 测试 agent，可用浏览器自动化操作你的 Web 应用。用自然语言描述测试，并实时查看执行步骤时间线。",
					},
				},
			},
			integrations: {
				devPass: {
					description:
						"面向 Claude Code、Cursor、Cline 和所有编码工具的固定月费方案。一个 API key，200+ 模型，账单可预测。",
					fromPrice: "每月 $29 起",
					everyModel: "包含所有模型",
				},
				badges: {
					planModeOnly: "仅计划模式",
				},
				items: {
					autohand:
						"在终端、IDE 和 Slack 中，将 doteb 与 Autohand Code 一起用于自主 AI 编码。",
					claudeCode:
						"将 doteb 与 Claude Code 一起用于 AI 驱动的终端辅助和编码。",
					cursor:
						"在计划模式中将 doteb 与 Cursor IDE 一起使用。Cursor 的编码 agent 不支持外部 API endpoint。",
					codex:
						"将 doteb 与 OpenAI 的 Codex CLI 一起用于 AI 驱动的终端编码。",
					cline: "将 doteb 与 Cline 一起用于 VS Code 中的 AI 编码辅助。",
					continue:
						"将 doteb 与 Continue 的开源 AI 代码助手 CLI 一起使用。",
					hermes:
						"将 doteb 与 Nous Research 的 Hermes Agent 一起用于终端 AI 编码。",
					kilo: "在 VS Code 中将 doteb 与 Kilo Code 一起用于自主 AI 编码，并使用其内置供应商支持。",
					kimi: "将 doteb 与 Kimi Code CLI 一起用于自主终端 AI 编码。",
					mimo: "将 doteb 与 MiMo Code CLI 一起用于自主终端 AI 编码。",
					n8n: "把 n8n 工作流自动化连接到 doteb，构建 AI 工作流。",
					opencode:
						"将 doteb 与 OpenCode CLI 一起用于 AI 驱动的开发工作流。",
					opencodeDesktop:
						"将 doteb 与 OpenCode Desktop 应用一起使用 - 通过 GUI 连接，无需配置文件。",
					openclaw:
						"将 doteb 与 OpenClaw 一起用于支持的聊天应用中的 AI 聊天。",
					pi: "将 doteb 与 Pi 编码 agent 一起使用，用任意模型进行 AI 终端编码。",
					vscode: "面向 AI 代码补全和聊天的原生 VS Code 集成。",
				},
			},
			apps: {
				page: {
					live: "实时 - 每 5 分钟刷新",
					titleLine1: "正在使用",
					titleLine2: "doteb 的应用",
					description:
						"真实编码 agent，真实流量。下面每个工具都通过一个 API 路由，并按处理 token 数排名。",
					appsTracked: "追踪应用",
					tokensProcessed: "已处理 token",
					requestsRouted: "已路由请求",
					listPromptPrefix: "想让你的应用出现在这里？在请求中设置",
					listPromptSuffix: "即可。",
					readDocs: "阅读文档",
					emptyTitle: "尚未记录到应用流量",
					emptyDescription:
						"请求开始流入后，应用会按处理 token 数排名显示在这里。",
				},
				grid: {
					categories: {
						all: "全部",
						coding: "编码 agent",
						automation: "自动化",
						other: "其他",
					},
					searchPlaceholder: "搜索应用...",
					noResultsTitle: "未找到应用",
					noResultsDescription: "请尝试其他搜索或筛选条件。",
					leaderboard: "排行榜",
					topThree: "按处理 token 数排名前三",
					results: "结果",
					moreApps: "更多应用",
					longTail: "长尾应用",
					app: "应用",
					tokens: "Token",
					requests: "请求",
					lastSeen: "最近出现",
					tokensShort: "token",
					tokensProcessed: "已处理 token",
					lastSeenPrefix: "最近出现：{time}",
					justNow: "刚刚",
					minutesAgo: "{value} 分钟前",
					hoursAgo: "{value} 小时前",
					daysAgo: "{value} 天前",
					monthsAgo: "{value} 个月前",
				},
				devPass: {
					badge: "这个排行榜上的每个应用都信任它",
					titleLine1: "一个 key。",
					titleLine2: "所有编码 agent。",
					descriptionBefore: "不要再维护九种不同订阅。DevPass 是",
					descriptionStrong: "一个固定价格",
					descriptionAfter:
						"覆盖 Claude Code、Cursor、Cline、OpenCode、Aider、Continue、Zed - 以及上面每个工具。同一个 key，所有 agent，任何编码位置都可用。",
					moreAgents: "以及当前可用的 14+ 个更多 agent",
					getDevPass: "获取 DevPass",
					seePlans: "查看方案 ->",
					dropInSetup: "即插即用设置",
				},
				metadata: {
					custom: "通过 doteb 发送流量的自定义集成。",
				},
			},
			timeline: {
				badge: "模型时间线",
				title: "doteb 模型支持时间线",
				description:
					"查看每个模型由供应商发布的时间，以及加入 doteb 的时间。",
				searchPlaceholder: "搜索模型、供应商、ID...",
				significant: "重要模型",
				newest: "最新",
				oldest: "最旧",
				providerRelease: "供应商发布时间",
				addedToGateway: "加入 doteb",
			},
			reliability: {
				hero: {
					badge: "可靠性",
					uptime: "99.9999% 有效可用率",
					title: "你的 AI 应用不能承受宕机。",
					description:
						"doteb 会实时把请求自动路由到健康供应商。当某个供应商故障时，流量会无缝故障转移 - 用户不会感知。",
					primaryCta: "几分钟内开始路由",
					effectiveUptime: "有效可用率",
					downtime: "每年停机时间",
					providers: "供应商",
					failoverOverhead: "故障转移开销",
				},
				failover: {
					badge: "工作原理",
					title: "毫秒级自动故障转移。",
					description:
						"每个请求都会被实时健康检查。一旦供应商开始失败、返回 5xx 或超时，流量会在同一个请求中转移到下一个健康供应商。",
					step1Title: "供应商失败",
					step1Description:
						"上游供应商返回 5xx、超时或限制你的请求。我们会在同一个请求周期内检测到。",
					step2Title: "即时重新路由",
					step2Description:
						"Gateway 会自动用同一个 prompt 重试该模型的下一个健康供应商，因此你的应用不会承受额外延迟。",
					step3Title: "返回响应",
					step3Description:
						"用户会得到答案。状态看板会为你记录该事件 - 即使供应商不可用，你的服务仍保持在线。",
					worksWithEveryRequest: "适用于每个请求",
					noChanges: "无需改 SDK。无需配置。",
				},
				features: {
					badge: "包含内容",
					title: "为生产流量而构建。",
					description:
						"可靠性是默认能力 - 不是附加功能。每个账号都能使用完整路由引擎。",
					healthTitle: "实时健康检查",
					healthDescription:
						"持续探测每个供应商，不健康的 endpoint 会在数秒内移出轮换。",
					routingTitle: "按延迟智能路由",
					routingDescription:
						"请求会发往你所在区域响应最快的供应商。TTFT 会按供应商和模型追踪。",
					regionsTitle: "多区域冗余",
					regionsDescription:
						"跨美国、欧盟和 APAC 等区域的供应商路由，区域故障也不会拖垮你的服务。",
					rateLimitTitle: "感知速率限制",
					rateLimitDescription:
						"当供应商限流时，流量会自动转移 - 你无需人工干预也能继续服务请求。",
					observableTitle: "默认可观测",
					observableDescription:
						"在 dashboard 中按供应商追踪可用率、错误率和延迟，可用于审计或分享给利益相关方。",
					slaTitle: "SLA 报告",
					slaDescription:
						"导出可用率和性能报告用于合规。企业方案包含带赔付额度的 99.9% SLA。",
				},
				cta: {
					title: "停止盯着供应商看板救火。",
					description:
						"把 base URL 切到 doteb，即可通过一行代码获得跨 25+ 供应商的自动故障转移、实时健康监控和可用率报告。",
				},
			},
			useCases: {
				index: {
					eyebrow: "用例",
					title: "你可以用 doteb 构建什么",
					description:
						"一个兼容 OpenAI 的 API，连接 200+ 模型 - 内置自动 fallback、prompt 缓存和逐请求成本分析。看看团队如何基于它构建。",
				},
				detail: {
					eyebrow: "用例",
					ctaTitle: "一个 API，连接每个模型",
					ctaDescription:
						"跨 200+ 模型路由，支持 fallback、缓存和逐请求成本分析。几分钟内免费开始。",
				},
				faqTitle: "常见问题",
			},
			migration: {
				index: {
					title: "迁移指南",
					description:
						"从其他 LLM 供应商迁移到 doteb，只需很少代码变更。兼容 OpenAI 的 API 让迁移更直接。",
					providerMissingTitle: "没看到你的供应商？",
					providerMissingDescription:
						"doteb 兼容 OpenAI API，适用于任何支持 OpenAI 的客户端。只需更改 base URL 和 API key。",
				},
				detail: {
					fromProvider: "来自 {provider}",
					published: "发布于",
					contentLabel: "迁移指南正文",
				},
			},
			features: {
				feature: "功能",
				getStarted: "开始使用",
				keyBenefits: "核心收益",
				useCases: "用例",
				liveDemo: "实时演示",
				readyTitle: "准备好开始了吗？",
				readyDescription: "加入正在用 doteb 驱动 AI 应用的数千名开发者。",
			},
			enterprise: {
				nav: "企业版",
				talkToSales: "联系销售",
				tryFree: "免费试用 doteb",
				whyTeams: "团队为什么启用它",
				howItWorks: "工作原理",
				howTitle: "从决策到上线，只需三个短步骤",
				realUseCases: "真实用例",
				realUseCasesTitle: "客户真正采用它的原因",
				faqTitle: "常见问题",
				moreCapabilities: "更多企业能力",
				moreCapabilitiesTitle: "企业级能力栈的其余部分",
				seeOnWorkloads: "在你的真实工作负载上体验 {feature}",
				workloadDescription:
					"带一个样例工作负载来参加 30 分钟通话。我们会现场接入，让你看到团队将获得的真实体验。",
			},
			tokenCalculator: {
				hero: {
					badge: "免费 LLM Token 成本计算器",
					title: "计算你的真实 LLM token 成本",
					description:
						"查看 GPT-4o、Claude、Gemini 和 200+ 模型按官方价格的成本，并比较通过 doteb 最低价供应商路由后能少付多少。",
					models: "200+ 模型",
					noMarkup: "零平台加价",
					freeNoSignup: "免费，无需注册",
				},
				form: {
					addModel: "再添加一个模型",
					selectModel: "选择模型",
					model: "模型",
					modelPlaceholder: "选择模型...",
					inputTokens: "输入 token",
					outputTokens: "输出 token",
					removeModel: "移除模型",
					presets: {
						light: "轻量（10K/1K）",
						medium: "中等（100K/10K）",
						heavy: "重度（1M/100K）",
						intensive: "高强度（10M/1M）",
					},
				},
				results: {
					officialPrice: "官方价格",
					llmGateway: "doteb",
					cheapestProvider: "最低价供应商",
					totalOfficial: "官方总成本",
					totalGateway: "使用 doteb 的总成本",
					youSave: "你可节省",
					noSavings: "此组合暂未发现节省空间",
					share: "分享",
				},
				cta: {
					highVolumeTitle: "处理高流量？",
					highVolumeDescription:
						"企业方案包含批量折扣、专属支持、自定义 SLA 和更长数据保留。",
					cutCostsTitle: "准备降低 LLM 成本了吗？",
					cutCostsDescription: "免费开始，无平台费，无需信用卡。",
				},
				content: {
					howTitle: "LLM 成本计算器如何工作",
					howDescription:
						"三步估算任意模型的月度花费，并查看通过 doteb 路由能节省多少。",
					step: "步骤 {number}",
					step1Title: "添加你使用的模型",
					step1Description:
						"从 OpenAI、Anthropic、Google、Mistral、Meta 等 200+ LLM 中选择。可添加任意数量模型并排比较。",
					step2Title: "输入 token 用量",
					step2Description:
						"设置预计处理的输入和输出 token，也可以从轻量、中等、重度或高强度预设开始再调整。",
					step3Title: "比较并开始节省",
					step3Description:
						"立即查看官方供应商价格与 doteb 最低价路由供应商，并得到每个周期可节省的确切金额。",
					understandingTitle: "理解 LLM token 成本",
					faqTitle: "常见问题",
					faqDescription: "关于估算和降低 LLM token 成本，你需要知道的一切。",
				},
			},
			nanoBanana: {
				hero: {
					badge: "成本模拟器",
					title: "Nano Banana Pro 成本模拟器",
					description:
						"计算 Gemini 3 Pro 图像生成的节省金额。相比 Google AI Studio 直连价格，doteb 提供 {discount}% 折扣。",
				},
				calculator: {
					label: "Google AI Studio 预计月度花费",
					googleDirect: "Google AI Studio 直连",
					fullPrice: "原价，无折扣",
					discountApplied: "已应用 {discount}% 折扣",
					savingsLabel: "预计每月节省",
					savingsBadge: "{discount}% 节省",
				},
				pricing: {
					badge: "价格拆解",
					title: "详细价格对比",
					metric: "指标",
					textInput: "文本输入",
					textOutput: "文本输出",
					cachedInput: "缓存输入",
					imageOutput: "图像输出",
					perImage: "每张图（约 1K）",
					note: "doteb 价格反映了相对 Google AI Studio 直连价格的 {discount}% 折扣。",
				},
				cta: {
					title: "今天开始节省图像生成成本",
					description:
						"通过 doteb 获得 Gemini 3 Pro 图像生成 {discount}% 折扣。开始无需信用卡。",
				},
			},
		},
	},
} satisfies UiMessageModule;
